import { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import CallAPI from "../../services/apiCalls";
import useUserStore from "../../utils/storeApp";

const getStatusSeverity = (status) => {
  switch (status) {
    case "Active":
      return "success";
    case "Pending":
      return "warning";
    default:
      return "danger";
  }
};

const dateBodyTemplate = (row) =>
  row.dateCreated
    ? new Date(row.dateCreated).toLocaleString("en-US", { hour12: false })
    : null;

const statusBodyTemplate = (row) => (
  <Tag value={row.status} severity={getStatusSeverity(row.status)}></Tag>
);

const usernameBodyTemplate = (row) => (
  <button
    type="button"
    className="btn btn-link border-0 align-baseline"
    disabled
    aria-label={`${row.userNameDisplay || "This user"} - details not available`}
  >
    <strong>{row.userNameDisplay}</strong>
  </button>
);

const GcpUserList = () => {
  const { userToken, sharedObject, SetSharedObject } = useUserStore((state) => ({
    userToken: state.userToken,
    sharedObject: state.sharedObject,
    SetSharedObject: state.SetSharedObject,
  }));
  const [customerObj, setCustomerObj] = useState([]);
  const [orgObj, setOrgObj] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [userObj, setUserObj] = useState([]);
  const [showGridFlag, setShowGridFlag] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState(false);

  useEffect(() => {
    setCustomerObj(sharedObject.vendorsObj || []);
  }, [sharedObject.vendorsObj]);

  const loadUsers = async (payload) => {
    setErrorMsg(false);
    setProcessing(true);
    const res = await CallAPI("/users/GetUsers", payload, "post", userToken);
    setProcessing(false);
    if (res.status === "SUCCESS") {
      setUserObj(res.data?.data?.pageItems || []);
      setShowGridFlag(true);
    } else {
      setErrorMsg(
        "Unable to load users for this organization. Please try again.",
      );
    }
  };

  useEffect(() => {
    const cached = sharedObject.UserIdEmail;
    if (cached && Object.keys(cached).length > 0 && cached.constructor === Object) {
      loadUsers(cached);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCustomerChange = (customer) => {
    setSelectedCustomer(customer);
    setSelectedOrg(null);
    setShowGridFlag(false);
    setErrorMsg(false);
    setUserObj([]);
    const orgs = (sharedObject.getOrgsObj || []).filter(
      (org) => org.vendorId === customer?.id,
    );
    setOrgObj(orgs);
  };

  const onOrgChange = async (org) => {
    setSelectedOrg(org);
    SetSharedObject("UserIdEmail", 0);
    await loadUsers({ OrganizationId: org?.id });
  };

  const pageTitle =
    sharedObject.UserIdEmail?.Flag === "orgEmails"
      ? `${sharedObject.UserIdEmail?.Name}`
      : sharedObject.UserIdEmail?.Flag === "adminUserOrgDetails"
        ? `Admins for  ${sharedObject.UserIdEmail?.Name}`
        : sharedObject.UserIdEmail?.Flag === "userOrgDetails"
          ? `Users for  ${sharedObject.UserIdEmail?.Name}`
          : `List`;

  return (
    <main className="ListUser">
      <div className="HeaderTools">
        <header className="ht-6">
          <div className="spacer-inline">
            <h4 className="title-label3" id="gcp-user-list-title">
              {pageTitle}
            </h4>
          </div>
          <div className="mid-header-bar">
            <div
              className="OrgDropdown spacer-inline"
              role="toolbar"
              aria-label="Filter users by customer and organization"
            >
              <label htmlFor="gcp-customer-dropdown" className="sr-only">
                Select Customer
              </label>
              <Dropdown
                inputId="gcp-customer-dropdown"
                value={selectedCustomer}
                onChange={(e) => onCustomerChange(e.value)}
                options={customerObj}
                optionLabel="name"
                placeholder="Customers"
                filter
                className="form-control w-md2 spacer-margin5"
              />

              <label htmlFor="gcp-org-dropdown" className="sr-only">
                Select Organization
              </label>
              <Dropdown
                inputId="gcp-org-dropdown"
                value={selectedOrg}
                onChange={(e) => onOrgChange(e.value)}
                options={orgObj}
                optionLabel="name"
                placeholder="Organizations"
                filter
                disabled={!selectedCustomer}
                className="form-control w-md2 spacer-margin5"
              />
            </div>
          </div>
        </header>
      </div>

      {processing && (
        <div
          className="loader-container"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          aria-labelledby="gcp-user-list-loader"
        >
          <div className="spinner" aria-hidden="true"></div>
          <div className="loading-text" id="gcp-user-list-loader">
            Loading...
          </div>
        </div>
      )}

      {!processing && errorMsg && (
        <p className="spacer-inline spacer-margin5" role="alert">
          {errorMsg}
        </p>
      )}

      {showGridFlag ? (
        <section className="grid-layout" aria-labelledby="gcp-user-list-title">
          <div>
            <DataTable
              value={userObj}
              paginator
              rows={10}
              rowsPerPageOptions={[10, 25, 50]}
              tableStyle={{ minWidth: "50rem", width: "100%" }}
              paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
              currentPageReportTemplate="{first} to {last} of {totalRecords}"
              dataKey="id"
              sortField="dateCreated"
              sortOrder={-1}
              aria-label="User List Table"
            >
              <Column
                field="userNameDisplay"
                header="Username"
                sortable
                aria-label="Username"
                body={usernameBodyTemplate}
              ></Column>
              <Column
                field="firstName"
                header="First Name"
                sortable
                aria-label="First Name"
              ></Column>
              <Column
                field="lastName"
                header="Last Name"
                sortable
                aria-label="Last Name"
              ></Column>
              <Column
                field="contactEmail"
                header="Contact Email"
                sortable
                aria-label="Contact Email"
              ></Column>
              <Column
                field="orgName"
                header="Organization"
                sortable
                aria-label="Organization"
              ></Column>
              <Column
                field="role"
                header="Role"
                sortable
                aria-label="Role"
              ></Column>
              <Column
                field="dateCreated"
                header="Date Created"
                className="w-md2"
                body={dateBodyTemplate}
                sortable
                aria-label="Date Created"
              ></Column>
              <Column
                field="status"
                header="Status"
                className="w-md11"
                body={statusBodyTemplate}
                sortable
                aria-label="Status"
              ></Column>
            </DataTable>
          </div>
        </section>
      ) : (
        <section
          className="empty-grid"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          There are currently no results.
        </section>
      )}
    </main>
  );
};

export default GcpUserList;
